import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useMemo } from "react";
import BigNumber from "bignumber.js";
import {
  calculatePercents,
  consolidatedToReadable,
  consolidateSummaryWithType,
  sortConsolidatedData,
} from "../helpers/dataHelper";
import { useDatesContext } from "context/DatesContext";

export const useOrderItemSummary = ({
  storeId,
  type = "fac",
  start,
  end,
  limit,
  sort,
  order,
  itemId,
}) => {
  const { orderItemSummary, loading } = useAllOrderItemSummary(
    storeId,
    type,
    start,
    end,
    itemId,
    limit,
    sort,
    order
  );

  // itemId
  // subcategoryId
  const orderItemSummaryConsolidated = useMemo(
    () =>
      consolidateSummaryWithType(
        orderItemSummary,
        (data) => `${data._id.subcategoryId}_${data._id.itemId}`,
        {
          quantityExtractor: (orderItem) => {
            return orderItem._id?.itemType !== "item"
              ? 0
              : parseFloat(orderItem.quantity, 10);
          },
          useThisDescription: (orderItem) => {
            return orderItem._id?.itemType === "item";
          },
        }
      ),
    [orderItemSummary]
  );

  return {
    loading,
    orderItemSummary: orderItemSummaryConsolidated,
  };
};

export const useOrderItemSubcategorySummary = ({
  storeId,
  type = "fac",
  start,
  end,
  limit,
  sort,
}) => {
  const { orderItemSubcategorySummary, loading } =
    useAllOrderItemSubcategorySummary(storeId, type, start, end, limit, sort);
  return {
    loading,
    orderItemSubcategorySummary,
  };
};

export const useOrderItemUserSummary = ({
  storeId,
  type = "fac",
  start,
  end,
  limit,
  sort,
}) => {
  const { orderItemUserSummary, loading } = useAllOrderItemUserSummary(
    storeId,
    type,
    start,
    end,
    limit,
    sort
  );
  return {
    loading,
    orderItemUserSummary,
  };
};

function useAllOrderItemSummary(
  storeId,
  type,
  start,
  end,
  itemId,
  limit,
  sort,
  order
) {
  //
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderItemSummary", 60 * 1000);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderItemSummary(
        $orderItemSummaryInput: OrderItemSummaryInput!
      ) {
        orderItemSummary(input: $orderItemSummaryInput) {
          _id {
            type
            itemType
            itemId
            itemName
            subcategoryId
            subcategoryName
            unitPrice
          }
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderItemSummaryInput: {
          storeId,
          type,
          start,
          end,
          itemId,
          limit,
          sort,
          order,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const orderItemSummary = data?.orderItemSummary ?? [];
  return { orderItemSummary, loading };
}

function useAllOrderItemSubcategorySummary(
  storeId,
  type,
  start,
  end,
  limit,
  sort
) {
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl(
    "useAllOrderItemSubcategorySummary",
    60 * 1000
  );
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderItemSubcategorySummary(
        $orderItemSubcategorySummaryInput: OrderItemSubcategorySummaryInput!
      ) {
        orderItemSubcategorySummary(input: $orderItemSubcategorySummaryInput) {
          _id {
            start
            end
            type
            subcategoryId
            subcategoryName
          }
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderItemSubcategorySummaryInput: {
          storeId,
          type,
          start,
          end,
          limit,
          sort,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  //
  const orderItemSubcategorySummary = data?.orderItemSubcategorySummary ?? [];
  return { orderItemSubcategorySummary, loading };
}

function useAllOrderItemUserSummary(storeId, type, start, end, limit, sort) {
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderItemUserSummary(
        $orderItemUserSummaryInput: OrderItemUserSummaryInput!
      ) {
        orderItemUserSummary(input: $orderItemUserSummaryInput) {
          _id {
            start
            end
            type
            userId
          }
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderItemUserSummaryInput: {
          storeId,
          type,
          start,
          end,
          limit,
          sort,
        },
      },
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  //
  const orderItemUserSummary = data?.orderItemUserSummary ?? [];
  return { orderItemUserSummary, loading };
}

export const useMostSoldItem = (orderItemSummary) => {
  return useMemo(() => {
    // convert to consolidated data
    /**
     * export type ConsolidatedData = {
     *   key: string;
     *   name: string;
     *   group?: string;
     *   count: number;
     *   quantity: BigNumber;
     *   total: BigNumber;
     *   quantity2: BigNumber;
     *   total2: BigNumber;
     *   quantity3: BigNumber;
     *   total3: BigNumber;
     *   percent?: BigNumber; -- sometimes
     * };
     *
     * {
     *     "__typename": "OrderItemSummaryPayloadItem",
     *     "_id": {
     *       "__typename": "OrderItemSummaryPayload_id",
     *       "start": "2023-08-08T06:00:00Z",
     *       "end": "2023-08-09T05:59:59.999Z",
     *       "type": "fac",
     *       "itemId": "63717f1b6cd3920a53cda0aa",
     *       "itemName": "IVA10",
     *       "subcategoryId": "625662409d8a8a4dd6efce08",
     *       "subcategoryName": "Subcat",
     *       "unitPrice": "110"
     *     },
     *     "total": "110",
     *     "tax1": "0",
     *     "quantity": "1",
     *     "count": 1
     *   }
     */

    const consolidatedDataMap = {};
    orderItemSummary.forEach((orderItemSummaryItem) => {
      const sign =
        orderItemSummaryItem._id?.type === "cre"
          ? new BigNumber(-1)
          : new BigNumber(1);
      const record = {
        key: orderItemSummaryItem._id.itemId,
        name: orderItemSummaryItem._id.itemName,
        group: orderItemSummaryItem._id.subcategoryName,
        count: new BigNumber(orderItemSummaryItem.count).multipliedBy(sign),
        quantity: new BigNumber(orderItemSummaryItem.quantity).multipliedBy(
          sign
        ),
        total: new BigNumber(orderItemSummaryItem.total).multipliedBy(sign),
      };
      if (!consolidatedDataMap[record.key]) {
        consolidatedDataMap[record.key] = record;
      } else {
        consolidatedDataMap[record.key].count = consolidatedDataMap[
          record.key
        ].count.plus(record.count);
        consolidatedDataMap[record.key].quantity = consolidatedDataMap[
          record.key
        ].quantity.plus(record.quantity);
        consolidatedDataMap[record.key].total = consolidatedDataMap[
          record.key
        ].total.plus(record.total);
      }
    });
    const consolidatedData = Object.keys(consolidatedDataMap).map(
      (key) => consolidatedDataMap[key]
    );
    return consolidatedToReadable(
      calculatePercents(
        sortConsolidatedData(consolidatedData, 99, (a, b) =>
          b.quantity.comparedTo(a.quantity)
        ),
        99
      )
    );
  }, [orderItemSummary]);
};
